import { useResolveContactQuery } from '@/hooks/queries/use-resolve-contact-query';
import { Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import type { ContactShell } from '../contact-types';
import { useContactResolveRef } from '../use-contact-resolve-ref';
import { RevealOnUnlock } from '@/components/motion/RevealOnUnlock';
import ContactDetailsList from './ContactDetailsList';
import ContactDetailsSkeleton from './ContactDetailsSkeleton';

interface ContactDetailsPanelProps {
  contact: ContactShell['contact'];
  /**
   * The brand's plan withholds this prospect's email and phone: the panel says
   * how they appear (Essential, or working together) and what is open now.
   */
  locked?: boolean;
}

/**
 * Body of the CONTACT rail section. While the contact identity is still
 * resolving it shows a skeleton rather than flashing the read-only fields; once
 * resolved it renders the editable details list. Saving a discovered creator to
 * the wishlist is driven by the identity-header star (`useContactWishlistStar`),
 * which re-resolves the drawer once the backend provisions the identity.
 */
const ContactDetailsPanel: React.FC<ContactDetailsPanelProps> = ({ contact, locked = false }) => {
  const { t } = useTranslation();
  const resolveRef = useContactResolveRef();
  const { isLoading: isResolving } = useResolveContactQuery(resolveRef);

  if (isResolving) return <ContactDetailsSkeleton />;
  // Mounted across the unlock, so the email fades in rather than popping in.
  return (
    <RevealOnUnlock locked={locked} delay={0.12}>
      {locked ? (
        <Text fontSize="13px" lineHeight="1.5" color="primary.600" py="6px">
          {t('planLocks.contact.body')}
        </Text>
      ) : (
        <ContactDetailsList contact={contact} />
      )}
    </RevealOnUnlock>
  );
};

export default ContactDetailsPanel;
